import isEqual from 'react-fast-compare';
import {Unsubscribe} from 'redux';

import * as StatusBar from 'components/StatusBar';
import {BulkAssemblyProcedure} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {patchBulkAssembly} from 'molbio/bulk-assembly/BulkAssemblyDAO';
import Notifications, {FOREVER_DURATION} from 'notifications';
import {getStore} from 'redux-stores/Store';
import lazyInit from 'util/lazyInit';
import {isServerRequestError} from 'util/Net';
import uniqueId from 'util/uniqueId';
import withWarnOnClose from 'util/withWarnOnClose';

// Track all bulk assembly IDs for which an auto-saver subscription is active. Use lazyInit so this
// state is reset between tests.
const getAutoSaverRunningIds = lazyInit(() => new Set<string>());

/**
 * Create a new subscription to save bulk assembly draft state to the server, if none already
 * exists for this bulk assembly ID.
 */
export function startBulkAssemblyAutoSaverIfNecessary(assemblyId: string): void {
  const runningIds = getAutoSaverRunningIds();
  if (runningIds.has(assemblyId)) {
    return;
  }
  runningIds.add(assemblyId);
  autoSaveBulkAssembly(assemblyId);
}

/**
 * Subscribe to Redux and send all relevant bulk assembly state changes to the server.
 *
 * All draft state is contained in the BulkAssemblyProcedure, which is stored in Redux and can be
 * saved to and loaded from the server directly. State changes are assumed to be user edits, and
 * as we're handling each edit, we consider the tab to be in an "unsaved changes" state and warn on
 * close and give a status bar message.
 *
 * On error, our current strategy is to leave a persistent red toast and keep trying on each future
 * edit, and give a temporary green toast if there's a successful save.
 */
function autoSaveBulkAssembly(assemblyId: string): void {
  let inErrorState = false;
  subscribeToBulkAssemblyEdits(assemblyId, async (procedure) => {
    await withWarnOnClose(async () => {
      StatusBar.showOverlayMessage('Saving...');
      try {
        await patchBulkAssembly(assemblyId, procedure);
        if (inErrorState) {
          Notifications.success('Saved.', {id: `bulk-assembly-${assemblyId}`});
          inErrorState = false;
        }
        StatusBar.setTimeoutMessage('Saved.');
      } catch (e) {
        StatusBar.showOverlayMessage('Error saving your assembly.');
        if (!isServerRequestError(e)) {
          throw e;
        }
        Notifications.error('There was an error saving your assembly.', {
          id: `bulk-assembly-${assemblyId}`,
          duration: FOREVER_DURATION,
        });
        inErrorState = true;
      }
    });
  });
}

/**
 * Subscribe to bulk assembly changes in a way such that each call to the listener indicates a new
 * version of the procedure that should be saved.
 *
 * This adds a few details that are a bit more specific than regular subscription:
 * - Initializing the procedure from empty and clearing the procedure are *not* considered events,
 *   since those don't indicate an edit to be saved.
 * - We use deep equality rather than reference equality to ensure that we don't call the promise on
 *   incidental actions that didn't change anything.
 * - The listener is an async function, and we make sure to never call it concurrently. If multiple
 *   events come in while the listener is running, we call it again just once with the last one.
 */
function subscribeToBulkAssemblyEdits(
  assemblyId: string,
  listener: (procedure: BulkAssemblyProcedure) => Promise<void>
): Unsubscribe {
  let oldProcedure: BulkAssemblyProcedure | null = null;
  let activePromise: Promise<unknown> | null = null;
  let nextInvocationId: string | null = null;
  return getStore().subscribe(async () => {
    const newProcedure = getStore().getState().ui.molbio.bulkAssemblies[assemblyId]?.procedure ?? null;
    // In addition to checking equality, skip a listener call if oldProcedure is null, since the initial
    // transition from null to a non-null value indicates initialization rather than edit.
    if (!oldProcedure || !newProcedure || isEqual(oldProcedure, newProcedure)) {
      oldProcedure = newProcedure;
      return;
    }
    oldProcedure = newProcedure;
    // In the case of multiple subscription events happening at once while a promise is outstanding,
    // use the nextInvocationId variable to agree which one will actually run. The last event to
    // set nextInvocationId will end up running, and the others will exit without doing anything.
    const invocationId = uniqueId('bulk-assembly-subscription');
    nextInvocationId = invocationId;
    if (activePromise) {
      await Promise.allSettled([activePromise]);
    }
    if (invocationId !== nextInvocationId) {
      return;
    }

    activePromise = listener(newProcedure);
    await activePromise;
    activePromise = null;
  });
}
